/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unused-vars, @typescript-eslint/strict-boolean-expressions */

import useLog from 'hooks/useLog';
import { useRecoilValue } from 'recoil';
import { userIsLoggedInState } from 'states';
import {
  NEW_POSITION_ID,
  Position,
  PositionShares,
  PositionDB,
} from 'types/optionsCalculator';

// Helper function to parse db position data
const parsePosition = (dbPosition: PositionDB): Position => {
  return {
    ...dbPosition,
    id: dbPosition.id ?? NEW_POSITION_ID,
    name: dbPosition.name ?? '',
    created_at: dbPosition.created_at
      ? new Date(dbPosition.created_at)
      : undefined,
    updated_at: dbPosition.updated_at
      ? new Date(dbPosition.updated_at)
      : undefined,
    sym:
      dbPosition.option_legs?.[0]?.sym ??
      dbPosition.underlying_shares?.[0]?.sym,
    option_legs: dbPosition.option_legs.map((leg: any) => ({
      ...leg,
      expiry: new Date(leg.expiry).getTime(),
    })),
    iv_adjustments: dbPosition.iv_adjustments.map((settings: any) => ({
      ...settings,
      expiry: new Date(settings.expiry).getTime(),
    })),
  };
};

const usePositions = () => {
  const { fetchAPIWithLog } = useLog('usePositions');
  const isLoggedIn = useRecoilValue(userIsLoggedInState);

  const getSavedPositions = async (): Promise<Position[]> => {
    if (!isLoggedIn) {
      return [];
    }
    const data = await fetchAPIWithLog('v1/me/positions');

    if (data.error != null) {
      throw new Error(data.error);
    }

    // Convert expiry dates to milliseconds for each position
    const positions = data.map(parsePosition);

    return positions as Position[];
  };

  // save position will always attempt to create a new position
  const savePosition = async (
    position: Position,
    sym: string,
    name: string,
  ): Promise<Position> => {
    if (!isLoggedIn) {
      throw new Error('User is not logged in');
    }
    // Strip leg IDs to create a new position if any ids are present
    const legsWithoutIds = position.option_legs?.map((leg) => {
      const { position_id, leg_id, expiry, ...legWithoutIds } = leg;
      return {
        ...legWithoutIds,
        expiry: new Date(expiry),
        opt_sym: sym,
      };
    });

    // Strip share IDs to create a new position if any ids are present
    const sharesWithoutIds: Partial<PositionShares>[] | undefined =
      position.underlying_shares?.map((share) => {
        const { position_id, ...shareWithoutId } = share;
        return shareWithoutId;
      });

    // Convert Map to array format for IV adjustments, stripping IDs if any ids are present
    const ivAdjustments = position.iv_adjustments?.map((settings) => {
      const { position_id, expiry, adj_id, ...settingsWithoutIds } = settings;
      return {
        ...settingsWithoutIds,
        expiry: new Date(expiry),
        sym,
      };
    });

    const positionWithoutId = {
      ...(legsWithoutIds.length > 0 && { option_legs: legsWithoutIds }),
      ...(sharesWithoutIds.length > 0 && {
        underlying_shares: sharesWithoutIds,
      }),
      ...(ivAdjustments.length > 0 && { iv_adjustments: ivAdjustments }),
      name,
    };

    const data = await fetchAPIWithLog('v1/me/position', {
      method: 'POST',
      body: JSON.stringify({ position: positionWithoutId }),
    });

    if (data.error != null) {
      throw new Error(data.error);
    }

    return parsePosition(data);
  };

  const editSavedPosition = async (
    position: Partial<Position>,
  ): Promise<Position> => {
    if (!isLoggedIn) {
      throw new Error('User is not logged in');
    }
    const payload: any = {
      position: {
        id: position.id,
        ...(position.name && { name: position.name }),
      },
    };

    // Only include legs if they exist
    if (position.option_legs != null) {
      const legsWithIds = position.option_legs?.map((leg) => {
        if (leg.leg_id < 0) {
          const { leg_id, ...legWithoutId } = leg;
          return {
            ...legWithoutId,
            expiry: new Date(leg.expiry),
          };
        }
        return {
          ...leg,
          expiry: new Date(leg.expiry),
        };
      });
      payload.position.option_legs = legsWithIds;
    }

    // Only include shares if they exist
    if (position.underlying_shares != null) {
      const sharesWithIds = position.underlying_shares?.map((share) => {
        return share;
      });
      payload.position.underlying_shares = sharesWithIds;
    }

    // Only include IV adjustments if they exist
    if (position.iv_adjustments != null) {
      const ivAdjustments = position.iv_adjustments?.map((settings) => {
        return {
          ...settings,
          expiry: new Date(settings.expiry),
        };
      });
      payload.position.iv_adjustments = ivAdjustments;
    }

    if (position.deletes != null) {
      payload.position.deletes = position.deletes;
    }

    const data = await fetchAPIWithLog('v1/me/position', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (data.error != null) {
      throw new Error(data.error);
    }

    return parsePosition(data);
  };

  const deleteSavedPosition = async (positionId: number): Promise<void> => {
    if (!isLoggedIn) {
      throw new Error('User is not logged in');
    }
    const data = await fetchAPIWithLog(`v1/me/positions?ids=${positionId}`, {
      method: 'DELETE',
    });

    if (data.error != null) {
      throw new Error(data.error);
    }
  };

  return {
    getSavedPositions,
    savePosition,
    editSavedPosition,
    deleteSavedPosition,
  };
};

export default usePositions;
